import React, { useState, useRef, useCallback } from 'react';
import {
  Search, Eye, EyeOff, Copy, CheckCircle, User,
  Shield, Key, AlertTriangle, RefreshCw, Lock, LogIn, Zap,
  ShieldCheck, ShieldX, ChevronRight, Mail
} from 'lucide-react';
import { API, authFetch } from '../lib/api';

// ── Role colour map ──────────────────────────────────────────────────────────
const ROLE_STYLE = {
  LICENSEE:     { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  MANAGER:      { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  LICENSOR:     { bg: '#faf5ff', text: '#7c3aed', border: '#ddd6fe' },
  ADMINSUPPORT: { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
};
const REQUIRED_ROLES = ['LICENSEE', 'MANAGER', 'LICENSOR', 'ADMINSUPPORT'];

// ── Inline password verifier ─────────────────────────────────────────────────
// Types a plaintext value and checks it against the stored bcrypt hash via API.
function VerifyInline({ hash }) {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [result, setResult]   = useState(null); // null | 'match' | 'no-match'
  const [checking, setChecking] = useState(false);

  const check = async () => {
    if (!input.trim()) return;
    setChecking(true); setResult(null);
    try {
      const r = await authFetch(`${API}/licensor/verify-hash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hash, plaintext: input })
      });
      const d = await r.json();
      setResult(d.match ? 'match' : 'no-match');
    } catch { setResult('no-match'); }
    setChecking(false);
  };

  if (!open) {
    return (
      <button
        data-testid="verify-hash-btn"
        onClick={() => { setOpen(true); setResult(null); setInput(''); }}
        title="Verify a plaintext password against this hash"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 9px', borderRadius: 5, cursor: 'pointer',
          border: '1px solid #c7d2fe', background: '#eef2ff',
          color: '#4f46e5', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
        }}
      >
        <ShieldCheck size={10} /> Verify
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <input
          autoFocus
          type="text"
          data-testid="verify-input"
          value={input}
          onChange={e => { setInput(e.target.value); setResult(null); }}
          onKeyDown={e => e.key === 'Enter' && check()}
          placeholder="Type plaintext password…"
          style={{
            flex: 1, padding: '5px 9px', border: '1.5px solid #c7d2fe', borderRadius: 6,
            fontSize: 12, fontFamily: 'monospace', outline: 'none', minWidth: 0,
          }}
          onFocus={e => { e.target.style.borderColor = '#6366f1'; }}
          onBlur={e  => { e.target.style.borderColor = '#c7d2fe'; }}
        />
        <button
          data-testid="verify-check-btn"
          onClick={check}
          disabled={!input.trim() || checking}
          style={{
            padding: '5px 11px', borderRadius: 6, border: 'none', cursor: (!input.trim() || checking) ? 'not-allowed' : 'pointer',
            background: (!input.trim() || checking) ? '#e2e8f0' : '#6366f1',
            color: (!input.trim() || checking) ? '#94a3b8' : '#fff',
            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {checking ? <RefreshCw size={10} style={{ animation: 'spin 0.8s linear infinite' }} /> : <ChevronRight size={10} />}
          {checking ? '' : 'Check'}
        </button>
        <button
          onClick={() => { setOpen(false); setResult(null); setInput(''); }}
          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 10, color: '#64748b' }}
        >✕</button>
      </div>
      {result === 'match' && (
        <div data-testid="verify-result-match" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#16a34a', fontWeight: 700, background: '#f0fdf4', borderRadius: 5, padding: '4px 10px', border: '1px solid #bbf7d0' }}>
          <ShieldCheck size={12} /> MATCH — this is the correct password
        </div>
      )}
      {result === 'no-match' && (
        <div data-testid="verify-result-nomatch" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#dc2626', fontWeight: 700, background: '#fef2f2', borderRadius: 5, padding: '4px 10px', border: '1px solid #fecaca' }}>
          <ShieldX size={12} /> NO MATCH — wrong password
        </div>
      )}
    </div>
  );
}

// ── Password reveal cell ─────────────────────────────────────────────────────
function PasswordCell({ value, testId, forceShow }) {
  const [show, setShow] = useState(true);   // revealed by default
  const [copied, setCopied] = useState(false);

  // forceShow drives from "Reveal All" toggle
  const isRevealed = forceShow || show;

  if (!value || value.length < 10) {
    return (
      <span style={{ color: '#ef4444', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
        <AlertTriangle size={11} /> MISSING
      </span>
    );
  }

  const isBcrypt = value.startsWith('$2');
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 260 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          {isRevealed ? (
            <span
              data-testid={testId}
              style={{
                fontFamily: 'monospace', fontSize: 10,
                color: isBcrypt ? '#16a34a' : '#d97706',
                background: isBcrypt ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${isBcrypt ? '#bbf7d0' : '#fde68a'}`,
                borderRadius: 4, padding: '3px 7px',
                display: 'block', wordBreak: 'break-all', lineHeight: 1.5,
              }}
            >
              {value}
            </span>
          ) : (
            <div
              data-testid={testId}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                background: isBcrypt ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${isBcrypt ? '#bbf7d0' : '#fde68a'}`,
                borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
              }}
              onClick={() => setShow(true)}
              title="Click to reveal password hash"
            >
              <span style={{ fontSize: 9, fontWeight: 700, color: isBcrypt ? '#16a34a' : '#d97706', flexShrink: 0 }}>
                {isBcrypt ? 'BCRYPT' : 'PLAIN'}
              </span>
              <span style={{ fontSize: 13, color: '#94a3b8', letterSpacing: '0.15em' }}>●●●●●●●●●●●●</span>
              <span style={{ fontSize: 9, color: '#94a3b8', flexShrink: 0 }}>click to reveal</span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShow(v => !v)}
          title={isRevealed ? 'Hide hash' : 'Reveal hash'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
            background: isRevealed ? '#e0f2fe' : '#f1f5f9',
            border: `1px solid ${isRevealed ? '#7dd3fc' : '#e2e8f0'}`,
            color: isRevealed ? '#0284c7' : '#64748b', flexShrink: 0,
          }}
        >
          {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        {isRevealed && (
          <button
            onClick={copy}
            title="Copy hash to clipboard"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
              background: copied ? '#f0fdf4' : '#f1f5f9',
              border: `1px solid ${copied ? '#bbf7d0' : '#e2e8f0'}`,
              color: copied ? '#16a34a' : '#64748b',
            }}
          >
            {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
          </button>
        )}
      </div>
      {/* Inline verifier — always shown when hash is visible */}
      {isRevealed && isBcrypt && <VerifyInline hash={value} />}
    </div>
  );
}

// ── Temp Password Panel ──────────────────────────────────────────────────────
function TempPasswordPanel({ clientId, childRecords, onDone }) {
  const [selectedUser, setSelectedUser] = useState('ALL');
  const [customPw, setCustomPw]         = useState('');
  const [loading, setLoading]           = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState('');
  const [copied, setCopied]             = useState(false);
  const [confirmed, setConfirmed]       = useState(false);

  // The user whose username we show in the login hint
  const targetUser = selectedUser === 'ALL'
    ? childRecords.find(r => r.UserType === 'LICENSEE')
    : childRecords.find(r => r.UserType === selectedUser);

  const apply = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await authFetch(`${API}/licensor/reset-tenant-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          userType: selectedUser,
          newPassword: customPw || undefined
        })
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || 'Reset failed');
      setResult(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const copyPw = () => {
    if (!result?.tempPassword) return;
    navigator.clipboard?.writeText(result.tempPassword).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div style={{ background: '#fff', border: '2px solid #6366f1', borderRadius: 10, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '5px 12px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <LogIn size={13} color="#fff" />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>ERP Login — Set Temp Password</span>
        <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>SUPPORT TOOL · SUPER ADMIN ONLY</span>
      </div>

      <div style={{ padding: '8px 12px' }}>

        {!result ? (
          <>
            {/* User selector */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>
                Apply temp password to which user?
              </label>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {/* ALL option */}
                <button
                  data-testid="user-select-ALL"
                  onClick={() => setSelectedUser('ALL')}
                  style={{
                    padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                    background: selectedUser === 'ALL' ? '#6366f1' : '#f1f5f9',
                    border: `1.5px solid ${selectedUser === 'ALL' ? '#6366f1' : '#e2e8f0'}`,
                    color: selectedUser === 'ALL' ? '#fff' : '#475569',
                  }}
                >
                  ALL USERS
                </button>
                {/* Individual user options */}
                {childRecords.map(u => (
                  <button
                    key={u.UserType}
                    data-testid={`user-select-${u.UserType}`}
                    onClick={() => setSelectedUser(u.UserType)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                      background: selectedUser === u.UserType ? '#0f172a' : '#f1f5f9',
                      border: `1.5px solid ${selectedUser === u.UserType ? '#0f172a' : '#e2e8f0'}`,
                      color: selectedUser === u.UserType ? '#fff' : '#475569',
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                    }}
                  >
                    <span style={{ fontSize: 8, opacity: 0.7 }}>{u.UserType}</span>
                    <span style={{ fontFamily: 'monospace' }}>{u.ClientName}</span>
                  </button>
                ))}
              </div>
              {targetUser && (
                <div style={{ marginTop: 5, fontSize: 10, color: '#4f46e5', background: '#eff6ff', borderRadius: 5, padding: '3px 8px', display: 'inline-block' }}>
                  Login as: <strong style={{ fontFamily: 'monospace' }}>{targetUser.ClientName}</strong>
                  {selectedUser === 'ALL' && <span style={{ color: '#64748b' }}> (LICENSEE) — or any of the 4 users below</span>}
                </div>
              )}
            </div>

            {/* Password input + apply */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 3 }}>
                  Temp Password (leave blank to auto-generate)
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                  <input
                    type="text"
                    value={customPw}
                    data-testid="temp-password-input"
                    onChange={e => setCustomPw(e.target.value)}
                    placeholder="Auto-generate (recommended)"
                    style={{ width: '100%', padding: '6px 10px 6px 26px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none' }}
                    onFocus={e => { e.target.style.borderColor = '#6366f1'; }}
                    onBlur={e  => { e.target.style.borderColor = '#e2e8f0'; }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10, color: '#64748b' }}>
                  <input type="checkbox" data-testid="temp-pw-confirm" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ accentColor: '#6366f1' }} />
                  I understand this overrides the existing ERP password
                </label>
                <button
                  data-testid="apply-temp-password-btn"
                  disabled={!confirmed || loading}
                  onClick={apply}
                  style={{
                    padding: '7px 16px', borderRadius: 7, border: 'none', fontWeight: 700,
                    fontSize: 12, cursor: (!confirmed || loading) ? 'not-allowed' : 'pointer',
                    background: (!confirmed || loading) ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: (!confirmed || loading) ? '#94a3b8' : '#fff',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {loading
                    ? <><RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }} /> Setting…</>
                    : <><Zap size={11} /> Set Password & Get Login</>}
                </button>
              </div>
            </div>
            {error && (
              <div style={{ marginTop: 6, padding: '5px 10px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', fontSize: 11, color: '#dc2626' }}>
                {error}
              </div>
            )}
          </>
        ) : (
          /* Success state */
          <div data-testid="temp-pw-result" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ padding: '8px 12px', background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                ✓ Password reset — {result.rowsUpdated} SaaS user{result.rowsUpdated !== 1 ? 's' : ''} updated
              </div>
              {/* DB sync status row */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 700 }}>
                  ✓ RC26MasterDB (SaaS auth)
                </span>
                <span data-testid="tenant-db-status" style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                  background: result.tenantDbUpdated ? '#dcfce7' : '#fef3c7',
                  color: result.tenantDbUpdated ? '#166534' : '#92400e' }}>
                  {result.tenantDbUpdated ? '✓' : '⚠'} {result.tenantDbError ? 'Tenant DB error' : result.tenantDbUpdated ? `Tenant DB (ERP) — ${result.tenantDbRows} user${result.tenantDbRows !== 1 ? 's' : ''} synced` : 'Tenant DB — legacy format (ERP auth via SaaS layer)'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ background: '#fff', border: '1px solid #d1fae5', borderRadius: 6, padding: '5px 10px' }}>
                    <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 1 }}>Username</div>
                    <code style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>{targetUser?.ClientName || result.users?.[0]?.ClientName || '—'}</code>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #d1fae5', borderRadius: 6, padding: '5px 10px' }}>
                    <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 1 }}>Temp Password</div>
                    <code data-testid="temp-pw-value" style={{ fontSize: 13, fontWeight: 800, color: '#4f46e5', fontFamily: 'monospace' }}>{result.tempPassword}</code>
                  </div>
                </div>
                <button
                  data-testid="copy-temp-pw-btn"
                  onClick={copyPw}
                  style={{ padding: '7px 14px', borderRadius: 7, border: '1.5px solid #86efac', cursor: 'pointer', background: copied ? '#dcfce7' : '#fff', color: copied ? '#16a34a' : '#374151', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  {copied ? <CheckCircle size={11} color="#16a34a" /> : <Copy size={11} />}
                  {copied ? 'Copied!' : 'Copy Password'}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {(selectedUser === 'ALL'
                ? result.users
                : result.users?.filter(u => u.UserType === selectedUser)
              )?.map(u => (
                <span key={u.UserType} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                  ✓ {u.ClientName} ({u.UserType})
                </span>
              ))}
            </div>
            <div style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', borderRadius: 5, padding: '5px 8px' }}>
              ⚠ {result.warning}
            </div>
            <button
              onClick={() => { setResult(null); setConfirmed(false); setCustomPw(''); onDone?.(); }}
              style={{ alignSelf: 'flex-start', padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 11, color: '#475569', fontWeight: 600 }}
            >
              Reset Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Credential health summary ────────────────────────────────────────────────
function HealthSummary({ childRecords }) {
  const roleMap = Object.fromEntries(childRecords.map(r => [r.UserType, r]));
  const issues = [];

  REQUIRED_ROLES.forEach(role => {
    if (!roleMap[role]) {
      issues.push({ type: 'error', msg: `${role} account is missing` });
    } else {
      const r = roleMap[role];
      if (!r.ClientPassword || r.ClientPassword.length < 10)
        issues.push({ type: 'error', msg: `${role} (${r.ClientName}) — ClientPassword missing, cannot log in` });
      else if (!r.ClientPassword.startsWith('$2'))
        issues.push({ type: 'warn', msg: `${role} (${r.ClientName}) — password is not bcrypt, may fail authentication` });
      if (!r.AltPassword || r.AltPassword.length < 10)
        issues.push({ type: 'warn', msg: `${role} (${r.ClientName}) — AltPassword missing` });
    }
  });

  const pwds = REQUIRED_ROLES.map(r => roleMap[r]?.ClientPassword).filter(Boolean);
  if (new Set(pwds).size > 1)
    issues.push({ type: 'warn', msg: 'Passwords are NOT consistent across all 4 roles — run Fix Data Quality to normalise' });

  if (issues.length === 0) {
    return (
      <div data-testid="health-ok" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <CheckCircle size={18} color="#16a34a" />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>All credentials healthy</div>
          <div style={{ fontSize: 11, color: '#16a34a' }}>4 roles present · bcrypt hashes · consistent passwords</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="health-issues" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <AlertTriangle size={13} /> Credential issues detected ({issues.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {issues.map((iss, i) => (
          <div key={i} style={{ fontSize: 11, display: 'flex', gap: 6 }}>
            <span style={{ fontWeight: 700, color: iss.type === 'error' ? '#ef4444' : '#d97706', flexShrink: 0 }}>
              {iss.type === 'error' ? '✗' : '⚠'}
            </span>
            <span style={{ color: '#78350f' }}>{iss.msg}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '6px 10px' }}>
        Fix: Go to DB Management → Run "Fix Data Quality" to normalise passwords across all roles
      </div>
    </div>
  );
}

// ── Tenant ERP DB Users Panel ─────────────────────────────────────────────────
// Shows actual UserMaster records from the physical tenant DB (item-master-config credentials)
function TenantDBPanel({ clientId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError('');
    try {
      const r = await authFetch(`${API}/licensor/tenant-db-users/${clientId}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setData(d);
      setExpanded(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId]);

  const statusColor = s => s === 'S' ? '#16a34a' : s === 'A' ? '#2563eb' : '#94a3b8';
  const statusBg    = s => s === 'S' ? '#f0fdf4' : s === 'A' ? '#eff6ff' : '#f8fafc';

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{ padding: '6px 10px', borderBottom: expanded ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: '#fafafa' }}
        onClick={() => { if (!data && !loading) load(); else setExpanded(v => !v); }}
      >
        <Key size={12} color="#0891b2" />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>Tenant ERP DB Users</span>
        <span style={{ fontSize: 9, color: '#64748b', marginLeft: 2 }}>— actual UserMaster credentials for ERP login (item-master-config)</span>
        {data && (
          <span style={{ fontSize: 9, fontWeight: 700, color: '#0891b2', background: '#e0f2fe', padding: '1px 6px', borderRadius: 6 }}>
            {data.users?.length} users · {data.dbName}
          </span>
        )}
        <button
          style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 5, border: '1px solid #e0f2fe', background: '#e0f2fe', color: '#0891b2', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
          onClick={e => { e.stopPropagation(); load(); }}
        >
          {loading ? <RefreshCw size={10} style={{ animation: 'spin 0.8s linear infinite' }} /> : expanded ? 'Refresh' : 'Load ERP Users'}
        </button>
      </div>

      {expanded && data && (
        <div style={{ padding: '6px 10px' }}>
          {/* Auth system clarification banner */}
          <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 10, color: '#92400e' }}>
            <strong>Two separate auth systems:</strong>
            {' '}<span style={{ color: '#1e293b' }}>Security Module (this platform) uses <code>ClientMaster_Child</code> with bcrypt. </span>
            <span style={{ color: '#1e293b' }}>ERP app (<code>item-master-config</code>) uses <code>UserMaster</code> with plaintext passwords.</span>
            {' '}Use <strong>Set Temp Password</strong> below to sync both systems.
          </div>

          {data.error && (
            <div style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 11, color: '#dc2626', marginBottom: 6 }}>
              Tenant DB error: {data.error}
            </div>
          )}

          {data.users?.length === 0 ? (
            <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>No UserMaster records found in tenant DB</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f0f9ff' }}>
                    {['CompId', 'UserId', 'UserName (ERP Login)', 'Status', 'Password'].map(h => (
                      <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 700, fontSize: 9, color: '#0891b2', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid #bae6fd' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f9ff', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11, color: '#374151', fontWeight: 600 }}>{u.CompId}</td>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11, color: '#374151', fontWeight: 700 }}>{u.UserId}</td>
                      <td style={{ padding: '4px 8px' }}>
                        <span data-testid={`tenant-username-${u.UserId}`} style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{u.UserName}</span>
                        <span style={{ marginLeft: 6, fontSize: 9, color: '#94a3b8' }}>← use this to login to ERP app</span>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: statusBg(u.Status), color: statusColor(u.Status), border: `1px solid ${statusBg(u.Status)}` }}>
                          {u.Status === 'S' ? 'Active' : u.Status === 'A' ? 'Active' : u.Status === '0' ? 'Inactive' : u.Status}
                        </span>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: u.PwdSet === 'SET' ? '#f0fdf4' : '#fef2f2', color: u.PwdSet === 'SET' ? '#16a34a' : '#ef4444' }}>
                          {u.PwdSet === 'SET' ? '✓ Set' : '✗ Empty'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 10, color: '#0891b2', background: '#e0f2fe', borderRadius: 5, padding: '4px 8px' }}>
            ERP login: use <strong>UserName</strong> above (not the Security Module username) + the temp password set below · DB: <code>{data.dbName}</code> ({data.dbType})
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function CredentialLookupPage() {
  const [search, setSearch]               = useState('');
  const [suggestions, setSuggestions]     = useState([]);
  const [showDrop, setShowDrop]           = useState(false);
  const [fetching, setFetching]           = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [client, setClient]               = useState(null);
  const [revealAll, setRevealAll]         = useState(true);
  const [emailSending, setEmailSending]   = useState(false);
  const [emailResult, setEmailResult]     = useState(null); // null | 'sent' | 'error'
  const debounce                          = useRef(null);

  const queryClients = useCallback((q) => {
    if (!q.trim()) { setSuggestions([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setFetching(true);
      try {
        const r    = await authFetch(`${API}/licensor/clients?search=${encodeURIComponent(q)}&limit=10`);
        const data = await r.json();
        const list = Array.isArray(data) ? data : data.clients || [];
        setSuggestions(list);
        setShowDrop(true);
      } catch { setSuggestions([]); }
      finally  { setFetching(false); }
    }, 280);
  }, []);

  const pickClient = async (c) => {
    setSearch(`${c.ClientId}  —  ${c.ClientName || c.CompName || ''}`);
    setShowDrop(false);
    setDetailLoading(true);
    setClient(null);
    try {
      const r    = await authFetch(`${API}/licensor/client/${c.ClientId}`);
      const data = await r.json();
      setClient({ ...data, _meta: c });
    } catch { setClient(null); }
    finally  { setDetailLoading(false); }
  };

  const children = client?.childRecords || [];
  const master   = client?.master       || {};

  const refresh = () => client && pickClient(client._meta || { ClientId: client.clientId || client.ClientId });

  const sendEmail = async () => {
    const clientId = master.ClientId || client?._meta?.ClientId;
    if (!clientId) return;
    const confirmed = window.confirm(
      `Send ERP credentials email to ${clientId}?\n\nThis will email all login credentials to the registered address for this account.\n\nOnly proceed if the account owner needs a credentials reminder.`
    );
    if (!confirmed) return;
    setEmailSending(true); setEmailResult(null);
    try {
      const r = await authFetch(`${API}/licensor/send-credentials-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
      });
      const d = await r.json();
      setEmailResult(d.ok ? 'sent' : 'error');
      setTimeout(() => setEmailResult(null), 5000);
    } catch { setEmailResult('error'); setTimeout(() => setEmailResult(null), 5000); }
    setEmailSending(false);
  };

  return (
    <div style={{ maxWidth: '100%', padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: 5 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
          background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Key size={13} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', margin: 0 }}>Client Credential Lookup</h2>
          <p style={{ fontSize: 10, color: '#64748b', margin: 0 }}>Search any client to view ERP usernames, encrypted password hashes and account health</p>
        </div>
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Shield size={9} color="#c2410c" />
          <span style={{ fontSize: 9, fontWeight: 700, color: '#c2410c' }}>ADMIN RESTRICTED</span>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={13} style={{ position: 'absolute', left: 10, color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            type="text"
            value={search}
            data-testid="credential-search-input"
            placeholder="Type Client ID (e.g. IN_LIT_0001) or company name…"
            onChange={e => { setSearch(e.target.value); queryClients(e.target.value); }}
            onFocus={e => { suggestions.length > 0 && setShowDrop(true); e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 2px rgba(99,102,241,0.12)'; }}
            onBlur={e  => { setTimeout(() => setShowDrop(false), 180); e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            style={{
              width: '100%', padding: '7px 36px 7px 32px',
              border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 13,
              outline: 'none', background: '#fff', boxSizing: 'border-box',
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
          />
          {fetching && (
            <RefreshCw size={12} style={{ position: 'absolute', right: 10, color: '#6366f1', animation: 'spin 0.8s linear infinite' }} />
          )}
        </div>
        {showDrop && suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
            {suggestions.map(c => (
              <button
                key={c.ClientId}
                data-testid={`sugg-${c.ClientId}`}
                onMouseDown={() => pickClient(c)}
                style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 8, padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <div style={{ width: 22, height: 22, borderRadius: 5, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={11} color="#3b82f6" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{c.ClientId}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>{c.ClientName || c.CompName || ''} · Tier: {c.tier_code || '—'}</div>
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: c.Status === 'ACTIVE' ? '#f0fdf4' : '#fff7ed', color: c.Status === 'ACTIVE' ? '#16a34a' : '#c2410c', border: `1px solid ${c.Status === 'ACTIVE' ? '#bbf7d0' : '#fed7aa'}` }}>
                  {c.Status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Detail loading ── */}
      {detailLoading && (
        <div style={{ textAlign: 'center', padding: 16, color: '#64748b', fontSize: 12 }}>
          <RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite', marginBottom: 4 }} /><div>Loading…</div>
        </div>
      )}

      {/* ── Client detail ── */}
      {client && !detailLoading && (
        <>
          {/* Master info strip */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {[
              { label: 'Client ID',  value: master.ClientId  || client._meta?.ClientId },
              { label: 'Company',    value: master.CompName  || master.ClientName || '—' },
              { label: 'Email',      value: master.EmailId   || '—' },
              { label: 'Tier',       value: master.tier_code || client._meta?.tier_code || '—' },
              { label: 'Status',     value: master.Status    || '—', color: master.Status === 'ACTIVE' ? '#16a34a' : master.Status === 'PENDING' ? '#d97706' : '#ef4444' },
              { label: 'SQL DB',     value: master.SQLDBName || '—' },
            ].map(f => (
              <div key={f.label} style={{ minWidth: 90 }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 1 }}>{f.label}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: f.color || '#0f172a' }}>{f.value}</div>
              </div>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {/* Email button */}
              <button
                data-testid="send-credentials-email-btn"
                onClick={sendEmail}
                disabled={emailSending}
                title="Email all credentials to msivan@myaccounts.in"
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                  borderRadius: 6, border: '1px solid',
                  borderColor: emailResult === 'sent' ? '#bbf7d0' : emailResult === 'error' ? '#fecaca' : '#c7d2fe',
                  background: emailResult === 'sent' ? '#f0fdf4' : emailResult === 'error' ? '#fef2f2' : '#eef2ff',
                  color: emailResult === 'sent' ? '#16a34a' : emailResult === 'error' ? '#dc2626' : '#4f46e5',
                  cursor: emailSending ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700,
                  transition: 'all 0.15s',
                }}
              >
                {emailSending
                  ? <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }} />
                  : emailResult === 'sent' ? <CheckCircle size={11} />
                  : <Mail size={11} />}
                {emailSending ? 'Sending…' : emailResult === 'sent' ? 'Sent!' : emailResult === 'error' ? 'Failed' : 'Email Credentials'}
              </button>
              <button
                data-testid="credential-refresh-btn"
                onClick={refresh}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#475569' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
              >
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
          </div>

          {/* ERP login accounts table */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '4px 10px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Shield size={12} color="#6366f1" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>ERP Login Accounts</span>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>— all 4 roles must have a bcrypt hash for ERP login to work</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '1px 6px', borderRadius: 6 }}>
                {children.length} user{children.length !== 1 ? 's' : ''}
              </span>
              <button
                data-testid="reveal-all-btn"
                onClick={() => setRevealAll(v => !v)}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, background: revealAll ? '#eff6ff' : '#f8fafc', border: `1px solid ${revealAll ? '#93c5fd' : '#e2e8f0'}`, color: revealAll ? '#1d4ed8' : '#475569' }}
              >
                {revealAll ? <EyeOff size={11} /> : <Eye size={11} />}
                {revealAll ? 'Hide Hashes' : 'Show Hashes'}
              </button>
            </div>

            {children.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No child user records found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table data-testid="credentials-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Role', 'Username (ERP login)', 'Status', 'ClientPassword (bcrypt)', 'AltPassword (bcrypt)'].map(h => (
                        <th key={h} style={{ padding: '4px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 700, fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((row, i) => {
                      const rs = ROLE_STYLE[row.UserType] || { bg: '#f8fafc', text: '#0f172a', border: '#e2e8f0' };
                      const ok = row.ClientPassword?.length > 10;
                      return (
                        <tr key={i} data-testid={`cred-row-${row.UserType}`} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '4px 8px' }}>
                            <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 9, fontWeight: 700, background: rs.bg, color: rs.text, border: `1px solid ${rs.border}` }}>{row.UserType}</span>
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <span data-testid={`username-${row.UserType}`} style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{row.ClientName}</span>
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: row.Status === 'ACTIVE' ? '#f0fdf4' : '#fff7ed', color: row.Status === 'ACTIVE' ? '#16a34a' : '#c2410c', border: `1px solid ${row.Status === 'ACTIVE' ? '#bbf7d0' : '#fed7aa'}` }}>{row.Status}</span>
                              {!ok && <span style={{ fontSize: 8, fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 1 }}><AlertTriangle size={8} /> NO PWD</span>}
                            </div>
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <PasswordCell value={row.ClientPassword} testId={`cpwd-${row.UserType}`} forceShow={revealAll} />
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <PasswordCell value={row.AltPassword} testId={`apwd-${row.UserType}`} forceShow={revealAll} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Health summary */}
          <HealthSummary childRecords={children} />

          {/* Tenant ERP DB users — shows actual UserMaster credentials */}
          <TenantDBPanel clientId={master.ClientId || client._meta?.ClientId} />

          {/* Temp Password / ERP Login Panel */}
          <TempPasswordPanel
            clientId={master.ClientId || client._meta?.ClientId}
            childRecords={children}
            onDone={refresh}
          />
        </>
      )}

      {/* ── Empty state ── */}
      {!client && !detailLoading && (
        <div style={{ textAlign: 'center', padding: '24px 12px', color: '#94a3b8' }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
            <Key size={16} style={{ opacity: 0.4 }} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Search for a client</div>
          <div style={{ fontSize: 11, maxWidth: 320, margin: '0 auto', lineHeight: 1.5 }}>
            Type a Client ID (e.g. <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>IN_LIT_0001</code>) or company name to view ERP credentials and diagnose login issues
          </div>
        </div>
      )}
    </div>
  );
}
